import * as React from 'react';
import { useState, useEffect } from 'react';

import { EditOutlined, UndoOutlined, CopyOutlined, InboxOutlined } from '@ant-design/icons';
import { Table, Button, Switch, Space, Popconfirm, Tag, Typography, Card, Row, Col, Select, DatePicker, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';

import { MOCK_ACCOUNTS } from '../../constants/data';
import { RuleTableItem, FormState, CampaignRule, Pagination, HierarchicalActionTypes, DictionariesResponse, CampaignRulesListRequest } from '../../types';
import { actionHuman } from '../../utils/actionHelpers';
import { summarizeBlock } from '../../utils/helpers';

dayjs.extend(isBetween);

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Компонент для изменяемого заголовка
const ResizableTitle = (props: any) => {
  const { onResize, width, ...restProps } = props;

  if (!width) {
    return <th {...restProps} />;
  }

  return (
    <th
      {...restProps}
      style={{
        ...restProps.style,
        position: 'relative',
        userSelect: 'none',
      }}
    >
      {restProps.children}
      <div
        style={{
          position: 'absolute',
          right: -5,
          top: 0,
          bottom: 0,
          width: 10,
          cursor: 'col-resize',
          zIndex: 1,
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          const startX = e.clientX;
          const startWidth = width;

          const handleMouseMove = (e: MouseEvent) => {
            const newWidth = Math.max(50, startWidth + e.clientX - startX);
            onResize(newWidth);
          };

          const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
          };

          document.addEventListener('mousemove', handleMouseMove);
          document.addEventListener('mouseup', handleMouseUp);
        }}
      />
    </th>
  );
};

interface RulesFilters {
  ruleName: string | undefined;
  accountName: string | undefined;
  budgetAction: string | undefined;
  dateRange: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null;
  createdBy: string | undefined;
}

interface RulesTableProps {
  rules: RuleTableItem[];
  apiRules?: CampaignRule[];
  loading?: boolean;
  pagination?: Pagination;
  onEdit: (rule: RuleTableItem) => void;
  onApiEdit?: (ruleId: number) => void;
  onDelete: (id: string) => void;
  onUnarchive?: (id: string) => void;
  onToggleStatus: (id: string, isActive: boolean) => void;
  onAdd: () => void;
  onDuplicate: (rule: RuleTableItem) => void;
  onApiDuplicate?: (ruleId: number) => void;
  onFiltersChange?: (filters: CampaignRulesListRequest) => void;
  onTableChange?: (pagination: any, filters: any, sorter: any) => void;
  scopeOne: 'campaigns' | 'adGroups' | 'ads';
  activeTab: 'active' | 'archived';
  actionTypes?: HierarchicalActionTypes;
  dictionaries?: DictionariesResponse['data'];
  onApiStatusToggle: (ruleId: number, isActive: boolean) => void;
  onApiArchiveToggle: (ruleId: number, archive: boolean) => void;
  sorting?: {
    sortField: CampaignRulesListRequest['sortField'];
    sortDirection: CampaignRulesListRequest['sortDirection'];
  };
}

export function RulesTable({
  rules,
  apiRules = [],
  loading = false,
  pagination,
  onEdit,
  onApiEdit,
  onDelete,
  onUnarchive,
  onToggleStatus,
  onAdd,
  onDuplicate,
  onApiDuplicate,
  onFiltersChange,
  onTableChange,
  scopeOne,
  activeTab,
  actionTypes,
  dictionaries,
  onApiStatusToggle,
  onApiArchiveToggle,
  sorting,
}: RulesTableProps) {
  const [showFilters, setShowFilters] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({
    name: 160,
    status: 100,
    account_name: 130,
    object_type: 120,
    scope: 90,
    conditions: 220,
    condition: 200,
    actions_summary: 130,
    action: 150,
    frequency: 100,
    createdAt: 100,
    created: 120,
    createdBy: 100,
    lastChecked: 150,
    lastCheck: 140,
    management: 140,
  });
  const [filters, setFilters] = useState<RulesFilters>({
    ruleName: undefined,
    accountName: undefined,
    budgetAction: undefined,
    dateRange: null,
    createdBy: undefined,
  });

  // Фильтруем правила: действующие - не архивированные, архивные - архивированные
  const activeRules = rules.filter(rule => !rule.isArchived);
  const archivedRules = rules.filter(rule => rule.isArchived);

  // Определяем какие правила показывать в зависимости от активной вкладки
  const displayRules = activeTab === 'active' ? activeRules : archivedRules;

  // Получаем названия правил для фильтра из API или локальных данных
  const uniqueRuleNames = React.useMemo(() => {
    if (dictionaries?.rule_names) {
      // Используем данные из API
      return dictionaries.rule_names.map(rule => ({
        label: rule.name,
        value: rule.name
      }));
    }
    // Fallback на локальные данные
    const names = [...new Set(displayRules.map(rule => rule.name))];
    return names.map(name => ({ label: name, value: name }));
  }, [displayRules, dictionaries]);

  // Получаем названия кабинетов для фильтра из API или локальных данных
  const uniqueAccountNames = React.useMemo(() => {
    if (dictionaries?.ad_accounts) {
      // Используем данные из API - объединяем все кабинеты из всех источников
      const allAccounts = Object.values(dictionaries.ad_accounts).flat();
      return allAccounts.map(account => ({
        label: account.name,
        value: account.name
      }));
    }
    // Fallback на локальные данные
    const accountIds = [...new Set(displayRules.flatMap(rule => rule.formData?.accounts || []))];
    const accounts = accountIds.map(id => MOCK_ACCOUNTS.find(acc => acc.id === id)).filter(Boolean);
    return accounts.map(acc => ({ label: acc!.name, value: acc!.name }));
  }, [displayRules, dictionaries]);

  // Получаем действия для фильтра из API или локальных данных
  const uniqueActions = React.useMemo(() => {
    if (dictionaries?.action_types) {
      // Используем данные из API - объединяем все действия из всех источников
      const allActions = Object.values(dictionaries.action_types).flatMap(sourceActions =>
        Object.values(sourceActions).flat()
      );
      return allActions.map(action => ({
        label: action.name,
        value: action.name
      }));
    }
    // Fallback на локальные данные
    return [
      { label: 'Ничего не меняем', value: 'none' },
      { label: 'Увеличиваем недельный бюджет', value: 'increase' },
      { label: 'Уменьшаем недельный бюджет', value: 'decrease' },
      { label: 'Отключаем', value: 'disable' },
    ];
  }, [dictionaries]);

  // Получаем создателей для фильтра из API или локальных данных
  const uniqueCreators = React.useMemo(() => {
    if (dictionaries?.employees) {
      // Используем данные из API
      return dictionaries.employees.map(employee => ({
        label: employee.name,
        value: employee.name
      }));
    }
    // Fallback на локальные данные
    const creators = [...new Set(displayRules.map(rule => rule.createdBy).filter(Boolean))];
    return creators.map(creator => ({ label: creator, value: creator }));
  }, [displayRules, dictionaries]);

  // Фильтруем данные
  const filteredRules = React.useMemo(() => {
    return displayRules.filter(rule => {
      // Фильтр по названию правила
      if (filters.ruleName && rule.name !== filters.ruleName) {
        return false;
      }

      // Фильтр по названию кабинета
      if (filters.accountName) {
        const ruleAccountNames = rule.formData?.accounts?.map(accountId =>
          MOCK_ACCOUNTS.find(acc => acc.id === accountId)?.name
        ).filter(Boolean) || [];
        if (!ruleAccountNames.includes(filters.accountName)) {
          return false;
        }
      }

      // Фильтр по действию
      if (filters.budgetAction && rule.actions.budgetAction !== filters.budgetAction) {
        return false;
      }

      // Фильтр по дате создания
      if (filters.dateRange && filters.dateRange[0] && filters.dateRange[1]) {
        const ruleDate = dayjs(rule.createdAt, 'DD.MM.YYYY');
        if (!ruleDate.isBetween(filters.dateRange[0], filters.dateRange[1], 'day', '[]')) {
          return false;
        }
      }

      // Фильтр по создателю
      if (filters.createdBy && rule.createdBy !== filters.createdBy) {
        return false;
      }

      return true;
    });
  }, [displayRules, filters]);

  const handleRuleNameChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, ruleName: value }));
  };

  const handleAccountNameChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, accountName: value }));
  };

  const handleBudgetActionChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, budgetAction: value }));
  };

  const handleDateRangeChange = (dates: [dayjs.Dayjs | null, dayjs.Dayjs | null] | null) => {
    setFilters(prev => ({ ...prev, dateRange: dates }));
  };

  const handleCreatedByChange = (value: string | undefined) => {
    setFilters(prev => ({ ...prev, createdBy: value }));
  };

  // Функция для преобразования локальных фильтров в API формат
  const convertFiltersToApiFormat = (localFilters: RulesFilters): CampaignRulesListRequest => {
    const apiFilters: CampaignRulesListRequest = {};

    // Фильтр по правилам
    if (localFilters.ruleName && dictionaries?.rule_names) {
      const rule = dictionaries.rule_names.find(r => r.name === localFilters.ruleName);
      if (rule) {
        apiFilters.rule_ids = [typeof rule.id === 'string' ? parseInt(rule.id) : rule.id];
      }
    }

    // Фильтр по кабинетам
    if (localFilters.accountName && dictionaries?.ad_accounts) {
      const allAccounts = Object.values(dictionaries.ad_accounts).flat();
      const account = allAccounts.find(acc => acc.name === localFilters.accountName);
      if (account) {
        apiFilters.ad_accounts = [typeof account.id === 'string' ? parseInt(account.id) : account.id];
      }
    }

    // Фильтр по действиям
    if (localFilters.budgetAction && dictionaries?.action_types) {
      const allActions = Object.values(dictionaries.action_types).flatMap(sourceActions =>
        Object.values(sourceActions).flat()
      );
      const action = allActions.find(act => act.name === localFilters.budgetAction);
      if (action) {
        apiFilters.action_type_ids = [action.id];
      }
    }

    // Фильтр по дате создания
    if (localFilters.dateRange && localFilters.dateRange[0] && localFilters.dateRange[1]) {
      apiFilters.created_from = localFilters.dateRange[0].format('YYYY-MM-DD');
      apiFilters.created_to = localFilters.dateRange[1].format('YYYY-MM-DD');
    }

    // Фильтр по создателю
    if (localFilters.createdBy && dictionaries?.employees) {
      const employee = dictionaries.employees.find(emp => emp.name === localFilters.createdBy);
      if (employee) {
        apiFilters.created_by_user_ids = [typeof employee.id === 'string' ? parseInt(employee.id) : employee.id];
      }
    }

    return apiFilters;
  };

  useEffect(() => {
    if (onFiltersChange && isInitialized) {
      const apiFilters = convertFiltersToApiFormat(filters);
      onFiltersChange(apiFilters);
    }
  }, [filters]);

  useEffect(() => {
    setIsInitialized(true);
  }, []);

  const applyFilters = () => {
    if (onFiltersChange) {
      const apiFilters = convertFiltersToApiFormat(filters);
      onFiltersChange(apiFilters);
    }
  };

  const clearFilters = () => {
    setFilters({
      ruleName: undefined,
      accountName: undefined,
      budgetAction: undefined,
      dateRange: null,
      createdBy: undefined,
    });
    // Очищаем API фильтры
    if (onFiltersChange) {
      onFiltersChange({});
    }
  };

  const handleResize = (key: string) => (width: number) => {
    setColumnWidths(prev => ({ ...prev, [key]: width }));
  };

  // Создаем колонки для локальных данных
  const createLocalColumns = (): ColumnsType<any> => {
    return [
      {
        title: 'Название правила',
        dataIndex: 'name',
        key: 'name',
        width: columnWidths.name,
        onHeaderCell: () => ({
          width: columnWidths.name,
          onResize: handleResize('name'),
        }),
        sorter: (a, b) => a.name.localeCompare(b.name),
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        render: (name: string) => (
          <div>
            <Text strong>{name}</Text>
          </div>
        ),
      },
      {
        title: 'Статус',
        dataIndex: 'isActive',
        key: 'isActive',
        width: columnWidths.status,
        onHeaderCell: () => ({
          width: columnWidths.status,
          onResize: handleResize('status'),
        }),
        sorter: (a, b) => (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0),
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        render: (isActive: boolean, record: RuleTableItem) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
            <Switch
              checked={isActive}
              onChange={(checked) => onToggleStatus(record.id, checked)}
              size="small"
            />
            <Tag color={isActive ? 'green' : 'orange'} style={{ fontSize: '11px' }}>
              {isActive ? 'Активно' : 'Неактивно'}
            </Tag>
          </div>
        ),
      },
      {
        title: 'Название кабинета',
        dataIndex: 'formData',
        key: 'account_name',
        width: columnWidths.account_name,
        onHeaderCell: () => ({
          width: columnWidths.account_name,
          onResize: handleResize('account_name'),
        }),
        render: (formData: any) => {
          const accountIds = formData?.accounts || [];
          const accounts = accountIds.map((id: string) => MOCK_ACCOUNTS.find(acc => acc.id === id)).filter(Boolean);
          return (
            <div>
              {accounts.map((acc: any, index: number) => (
                <Tag key={index} style={{ marginBottom: 2 }}>
                  {acc.name}
                </Tag>
              ))}
            </div>
          );
        },
      },
      {
        title: 'Что проверяем?',
        dataIndex: 'formData',
        key: 'object_type',
        width: columnWidths.object_type,
        onHeaderCell: () => ({
          width: columnWidths.object_type,
          onResize: handleResize('object_type'),
        }),
        render: (formData: any) => {
          const scope = formData?.scope?.[0] || 'campaigns';
          const scopeLabels = {
            campaigns: 'Кампании',
            adGroups: 'Группы объявлений',
            ads: 'Объявления'
          };
          return <Tag color="blue">{scopeLabels[scope as keyof typeof scopeLabels]}</Tag>;
        },
      },
      {
        title: 'Условие',
        dataIndex: 'groups',
        key: 'condition',
        width: columnWidths.condition,
        onHeaderCell: () => ({
          width: columnWidths.condition,
          onResize: handleResize('condition'),
        }),
        render: (groups: any[]) => {
          const conditionText = groups.map(group =>
            group.atoms.map((atom: any) =>
              `${atom.metric} ${atom.comparator} ${atom.value} за последние ${atom.periodDays} дней`
            ).join(' ИЛИ ')
          ).join(' И ');
          return <Text style={{ fontSize: '12px' }}>{conditionText}</Text>;
        },
      },
      {
        title: 'Делаем',
        dataIndex: 'actions',
        key: 'action',
        width: columnWidths.action,
        onHeaderCell: () => ({
          width: columnWidths.action,
          onResize: handleResize('action'),
        }),
        render: (actions: any) => {
          const actionText = actionHuman(scopeOne, actions, actionTypes);
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: '12px' }}>{actionText}</Text>
              {actions.sendTelegram && (
                <Button size="small" type="primary" style={{ fontSize: '10px', height: '20px', padding: '0 6px' }}>
                  TG
                </Button>
              )}
            </div>
          );
        },
      },
      {
        title: 'Частота срабатывания',
        dataIndex: 'periodDays',
        key: 'frequency',
        width: columnWidths.frequency,
        onHeaderCell: () => ({
          width: columnWidths.frequency,
          onResize: handleResize('frequency'),
        }),
        render: (days: number) => (
          <Text>Каждые {days} дн.</Text>
        ),
      },
      {
        title: 'Дата создания',
        dataIndex: 'createdAt',
        key: 'created',
        width: columnWidths.created,
        onHeaderCell: () => ({
          width: columnWidths.created,
          onResize: handleResize('created'),
        }),
        sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        render: (date: string, record: RuleTableItem) => (
          <div>
            <div style={{ fontSize: '12px' }}>{date}</div>
            <div style={{ fontSize: '11px', color: '#666' }}>{record.createdBy}</div>
          </div>
        ),
      },
      {
        title: 'Кто создал',
        dataIndex: 'createdBy',
        key: 'created_by',
        width: columnWidths.createdBy,
        onHeaderCell: () => ({
          width: columnWidths.createdBy,
          onResize: handleResize('createdBy'),
        }),
        sorter: (a, b) => a.createdBy.localeCompare(b.createdBy),
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        render: (creator: string) => (
          <Text style={{ fontSize: '12px' }}>{creator}</Text>
        ),
      },
      {
        title: 'Дата и время последней проверки',
        dataIndex: 'lastChecked',
        key: 'last_check',
        width: columnWidths.lastCheck,
        onHeaderCell: () => ({
          width: columnWidths.lastCheck,
          onResize: handleResize('lastCheck'),
        }),
        render: (date: string) => (
          <Text style={{ fontSize: '12px' }}>{date || 'Не проверялось'}</Text>
        ),
      },
      {
        title: 'Управление',
        key: 'actions',
        width: 120,
        fixed: 'right',
        render: (_, record: RuleTableItem) => (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record)}
              title="Редактировать"
            />
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={() => onDuplicate(record)}
              title="Дублировать"
            />
            {activeTab === 'archived' ? (
              <Popconfirm
                title="Разархивировать правило? Правило будет перенесено обратно в действующие"
                onConfirm={() => onUnarchive?.(record.id)}
                okText="Разархивировать"
                cancelText="Отмена"
              >
                <Button
                  type="text"
                  icon={<UndoOutlined />}
                  size="small"
                  title="Разархивировать правило"
                />
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Архивировать правило? Правило будет перенесено в архив и скрыто из основного списка"
                onConfirm={() => onDelete(record.id)}
                okText="Архивировать"
                cancelText="Отмена"
              >
                <Button
                  type="text"
                  icon={<InboxOutlined />}
                  size="small"
                  title="Архивировать правило"
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ];
  };

  // Создаем колонки для API данных (адаптированные локальные колонки)
  const createApiColumns = (): ColumnsType<any> => {
    return [
      {
        title: 'Название правила',
        dataIndex: 'rule_name',
        key: 'rule_name',
        width: columnWidths.name,
        onHeaderCell: () => ({
          width: columnWidths.name,
          onResize: handleResize('name'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'rule_name' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (name: string) => (
          <div>
            <Text strong>{name}</Text>
          </div>
        ),
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        key: 'status',
        width: columnWidths.status,
        onHeaderCell: () => ({
          width: columnWidths.status,
          onResize: handleResize('status'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        sortOrder: sorting?.sortField === 'status_name' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        render: (status: { id: number | null, name: string | null }, record: CampaignRule) => {
          const isActive = status?.id === 1;
          const statusName = status?.name || 'Неизвестно';
          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'center' }}>
              <Switch
                checked={isActive}
                onChange={(checked) => onApiStatusToggle(record.rule_id, checked)}
                size="small"
              />
              <Tag color={isActive ? 'green' : 'orange'} style={{ fontSize: '11px' }}>
                {statusName}
              </Tag>
            </div>
          );
        },
      },
      {
        title: 'Название кабинета',
        dataIndex: 'ad_accounts_names',
        key: 'ad_accounts_names',
        width: columnWidths.account_name,
        onHeaderCell: () => ({
          width: columnWidths.account_name,
          onResize: handleResize('account_name'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'ad_accounts_names' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (accounts: string | string[] | null) => {
          if (!accounts) {
            return <Text type="secondary">—</Text>;
          }

          try {
            let accountsList: string[] = [];

            if (typeof accounts === 'string') {
              let processedString = accounts.trim();

              if (processedString.startsWith('{') && processedString.endsWith('}')) {
                processedString = processedString.slice(1, -1).trim();
              }

              if (processedString.startsWith('"') && processedString.endsWith('"')) {
                processedString = processedString.slice(1, -1);
              }

              if (processedString.length > 0) {
                accountsList = [processedString];
              }
            } else if (Array.isArray(accounts)) {
              accountsList = accounts;
            }

            accountsList = accountsList.filter(account =>
              account && typeof account === 'string' && account.trim().length > 0
            );

            if (accountsList.length === 0) {
              return <Text type="secondary">—</Text>;
            }

            return (
              <Text type="secondary">
                {accountsList.join(', ')}
              </Text>
            );
          } catch (error) {
            console.error('Error rendering ad_accounts_names:', error, accounts);
            return <Text type="secondary">Ошибка отображения</Text>;
          }
        },
      },
      {
        title: 'Что проверяем?',
        dataIndex: 'object_type_name',
        key: 'object_type_name',
        width: columnWidths.object_type,
        onHeaderCell: () => ({
          width: columnWidths.object_type,
          onResize: handleResize('object_type'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'object_type_name' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (type: string) => (
          <Text type="secondary">{type}</Text>
        ),
      },
      {
        title: 'Условие',
        dataIndex: 'criterion_text',
        key: 'criterion_text',
        width: columnWidths.condition,
        onHeaderCell: () => ({
          width: columnWidths.condition,
          onResize: handleResize('condition'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'criterion_text' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (text: string) => (
          <Text type="secondary" style={{ fontSize: 12 }}>
            ({text})
          </Text>
        ),
      },
      {
        title: 'Делаем',
        dataIndex: 'action_type_name',
        key: 'action_type_name',
        width: columnWidths.action,
        onHeaderCell: () => ({
          width: columnWidths.action,
          onResize: handleResize('action'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'action_type_name' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (action: string) => (
          <Text type="secondary" style={{ fontSize: 12 }}>{action}</Text>
        ),
      },
      {
        title: 'Частота срабатывания',
        dataIndex: 'action_frequency',
        key: 'action_frequency',
        width: columnWidths.frequency,
        onHeaderCell: () => ({
          width: columnWidths.frequency,
          onResize: handleResize('frequency'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'action_frequency' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (frequency: number) => (
          <Text type="secondary">Каждые {frequency} дн.</Text>
        ),
      },
      {
        title: 'Дата создания',
        dataIndex: 'created_date',
        key: 'created_date',
        width: columnWidths.created,
        onHeaderCell: () => ({
          width: columnWidths.created,
          onResize: handleResize('created'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'created_date' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (date: string, record: CampaignRule) => (
          <Text type="secondary">{date}</Text>
        ),
      },
      {
        title: 'Кто создал',
        dataIndex: 'created_by_name',
        key: 'created_by_name',
        width: columnWidths.createdBy,
        onHeaderCell: () => ({
          width: columnWidths.createdBy,
          onResize: handleResize('createdBy'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'created_by_name' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (creator: string) => (
          <Text type="secondary">{creator}</Text>
        ),
      },
      {
        title: 'Дата и время последней проверки',
        dataIndex: 'last_check_date',
        key: 'last_check_date',
        width: columnWidths.lastCheck,
        onHeaderCell: () => ({
          width: columnWidths.lastCheck,
          onResize: handleResize('lastCheck'),
        }),
        sorter: true,
        sortDirections: ['ascend', 'descend'],
        showSorterTooltip: { title: 'Нажмите для сортировки' },
        sortOrder: sorting?.sortField === 'last_check_date' ? (sorting.sortDirection === 'asc' ? 'ascend' : 'descend') : null,
        render: (date: string) => (
          <Text type="secondary">{date || 'Не проверялось'}</Text>
        ),
      },
      {
        title: 'Управление',
        key: 'actions',
        width: 120,
        fixed: 'right',
        render: (_, record: CampaignRule) => (
          <Space size="small">
            <Button
              type="text"
              icon={<EditOutlined />}
              size="small"
              onClick={() => {
                if (onApiEdit) {
                  onApiEdit(record.rule_id);
                } else {
                  message.info('Редактирование правил из API будет доступно в следующих версиях');
                }
              }}
              title="Редактировать"
            />
            <Button
              type="text"
              icon={<CopyOutlined />}
              size="small"
              onClick={() => {
                if (onApiDuplicate) {
                  onApiDuplicate(record.rule_id);
                } else {
                  message.info('Дублирование правил из API будет доступно в следующих версиях');
                }
              }}
              title="Дублировать"
            />
            {activeTab === 'archived' ? (
              <Popconfirm
                title="Разархивировать правило? Правило будет перенесено обратно в действующие"
                onConfirm={() => onApiArchiveToggle(record.rule_id, false)}
                okText="Разархивировать"
                cancelText="Отмена"
              >
                <Button
                  type="text"
                  icon={<UndoOutlined />}
                  size="small"
                  title="Разархивировать правило"
                />
              </Popconfirm>
            ) : (
              <Popconfirm
                title="Архивировать правило? Правило будет перенесено в архив и скрыто из основного списка"
                onConfirm={() => onApiArchiveToggle(record.rule_id, true)}
                okText="Архивировать"
                cancelText="Отмена"
              >
                <Button
                  type="text"
                  icon={<InboxOutlined />}
                  size="small"
                  title="Архивировать правило"
                />
              </Popconfirm>
            )}
          </Space>
        ),
      },
    ];
  };

  // Создаем колонки в зависимости от источника данных
  const createColumns = (): ColumnsType<any> => {
    if (apiRules.length > 0) {
      // Используем специальные колонки для API данных
      return createApiColumns();
    }

    // Колонки для локальных данных (существующая логика)
    return createLocalColumns();
  };

  const columns = createColumns();

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <Text
            style={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? 'Скрыть фильтры' : 'Показать фильтры'}
          </Text>
        </div>
      </div>

      {showFilters && (
        <Card size="small" style={{ marginBottom: 16, border: '1px solid #d9d9d9', borderRadius: '8px' }}>
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Row gutter={[16, 16]}>
              <Col span={4}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text>Правило</Text>
                  <Select
                    value={filters.ruleName}
                    onChange={handleRuleNameChange}
                    placeholder="Выберите правило"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string).toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                    options={uniqueRuleNames}
                  />
                </Space>
              </Col>
              <Col span={5}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text>Название кабинета</Text>
                  <Select
                    value={filters.accountName}
                    onChange={handleAccountNameChange}
                    placeholder="Выберите кабинет"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string).toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                    options={uniqueAccountNames}
                  />
                </Space>
              </Col>
              <Col span={4}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text>Что делаем</Text>
                  <Select
                    value={filters.budgetAction}
                    onChange={handleBudgetActionChange}
                    placeholder="Выберите действие"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string).toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                    options={uniqueActions}
                  />
                </Space>
              </Col>
              <Col span={6}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text>Дата создания</Text>
                  <RangePicker
                    value={filters.dateRange as any}
                    onChange={handleDateRangeChange as any}
                    format="DD.MM.YYYY"
                    placeholder={['Дата от', 'Дата до']}
                    style={{ width: '100%' }}
                  />
                </Space>
              </Col>
              <Col span={5}>
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Text>Кто создал</Text>
                  <Select
                    value={filters.createdBy}
                    onChange={handleCreatedByChange}
                    placeholder="Выберите создателя"
                    allowClear
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label as string).toLowerCase().includes(input.toLowerCase())
                    }
                    style={{ width: '100%' }}
                    dropdownMatchSelectWidth={false}
                    options={uniqueCreators}
                  />
                </Space>
              </Col>
            </Row>
            {(filters.ruleName || filters.accountName || filters.budgetAction || filters.dateRange || filters.createdBy) && (
              <div style={{ textAlign: 'right' }}>
                <Text
                  type="secondary"
                  style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={clearFilters}
                >
                  Очистить фильтры
                </Text>
              </div>
            )}
          </Space>
        </Card>
      )}

      <div style={{ border: '1px solid #d9d9d9', borderRadius: '8px', overflow: 'hidden' }}>
        <Table
          components={{
            header: {
              cell: ResizableTitle,
            },
          }}
          columns={createColumns()}
          dataSource={apiRules}
          rowKey="rule_id"
          loading={loading}
          onChange={onTableChange}
          pagination={pagination ? {
            current: pagination.page,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} ${total === 1 ? 'правила' : total < 5 ? 'правил' : 'правил'}`,
            style: { paddingRight: 16 },
          } : {
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} ${total === 1 ? 'правила' : total < 5 ? 'правил' : 'правил'}`,
            style: { paddingRight: 16 },
          }}
          size="small"
          bordered
        />
      </div>
    </div>
  );
}
